import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles, getLatestArticles } from '@/mocks/articles';
import SEOMeta from '@/components/feature/SEOMeta';

export default function HomePage() {
  const featuredArticles = getFeaturedArticles();
  const latestArticles = getLatestArticles(6);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '不良研究所',
    url: 'https://buliangyanjiusuo.com',
    description: '深度研究与内容探索平台，汇聚多元视角与独立思考，带你发现主流之外的真实世界。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://buliangyanjiusuo.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '不良研究所',
    url: 'https://buliangyanjiusuo.com',
    description: '专注深度内容研究与创意探索的平台',
    sameAs: [
      'https://weibo.com/buliangyanjiusuo',
      'https://space.bilibili.com/buliangyanjiusuo',
    ],
  };

  return (
    <>
      <SEOMeta
        title="不良研究所 - 深度研究与内容探索平台"
        description="不良研究所是一个专注深度内容研究与创意探索的平台，汇聚多元视角与独立思考，带你发现主流之外的真实世界。在不良研究所，每一篇内容都是一次严肃而有趣的研究旅程。"
        keywords="不良研究所,不良研究所官网,不良研究所内容平台,深度研究内容,独立创意研究,非主流内容探索,研究所风格内容"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([jsonLd, orgJsonLd]) }}
      />

      <HeroSection />
      <FeaturedArticles featuredArticles={featuredArticles} />
      <CategoryNav categories={categories} />
      <LatestArticles latestArticles={latestArticles} />
      <AboutSnippet />
      <NewsletterSection />
    </>
  );
}

function HeroSection() {
  return (
    <section className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20intellectual%20atmosphere%20with%20warm%20cream%20and%20terracotta%20gradients%2C%20minimalist%20geometric%20patterns%20evoking%20research%20and%20discovery%2C%20soft%20ambient%20light%2C%20editorial%20style%2C%20calm%20and%20thoughtful%20composition&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
          alt="不良研究所 - 深度研究内容平台背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
      </div>

      <div className="relative z-10 w-full text-center px-4 md:px-6 max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight"
          style={{ fontFamily: 'var(--font-heading)' }}>
          每一篇内容，都是一次<br className="hidden md:block" />严肃而有趣的研究旅程
        </h1>
        <p className="mt-5 text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
          不良研究所汇聚多元视角与独立思考，在信息洪流中为你筛选值得深读的内容，
          带你发现主流叙事之外的真实世界。
        </p>
        <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
          <Link
            to="/category/shehui-guancha"
            className="px-6 py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            开始探索
          </Link>
          <Link
            to="/about"
            className="px-6 py-3 bg-white/15 text-white border border-white/25 rounded-md text-sm font-medium hover:bg-white/25 transition-colors whitespace-nowrap"
          >
            了解我们
          </Link>
        </div>

        <div className="mt-12 flex items-center justify-center gap-8 text-white/60 text-sm">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">12+</p>
            <p className="mt-1">深度文章</p>
          </div>
          <div className="w-px h-10 bg-white/20"></div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">5</p>
            <p className="mt-1">研究领域</p>
          </div>
          <div className="w-px h-10 bg-white/20"></div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">8</p>
            <p className="mt-1">核心作者</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedArticles({ featuredArticles }: { featuredArticles: ReturnType<typeof getFeaturedArticles> }) {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            精选研究
          </h2>
          <p className="mt-2 text-sm text-foreground-600">编辑团队为你精选的深度内容</p>
        </div>
        <Link
          to="/category/shehui-guancha"
          className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
        >
          查看全部 <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {featuredArticles.map((article) => (
          <Link
            key={article.slug}
            to={`/article/${article.slug}`}
            className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-colors duration-200"
          >
            <div className="aspect-[16/9] overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5 md:p-6">
              <span className="inline-block text-xs font-medium text-primary-600 bg-primary-100/70 px-2.5 py-1 rounded-full mb-3">
                {article.tags[0]}
              </span>
              <h3 className="text-lg md:text-xl font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
                {article.title}
              </h3>
              <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-4">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.publishDate}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function CategoryNav({ categories }: { categories: typeof import('@/mocks/categories').categories }) {
  return (
    <section className="bg-background-100 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            探索研究领域
          </h2>
          <p className="mt-2 text-sm text-foreground-600">五大领域，覆盖深度内容探索的方方面面</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex flex-col items-center text-center p-6 rounded-lg bg-background-50 hover:bg-primary-100/50 transition-colors duration-200"
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                <i className={`${cat.icon} text-xl`}></i>
              </div>
              <h3 className="mt-4 text-sm font-semibold text-foreground-900">
                {cat.name}
              </h3>
              <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">
                {cat.description}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestArticles({ latestArticles }: { latestArticles: ReturnType<typeof getLatestArticles> }) {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            最新研究
          </h2>
          <p className="mt-2 text-sm text-foreground-600">持续更新的深度内容</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {latestArticles.map((article) => (
          <Link
            key={article.slug}
            to={`/article/${article.slug}`}
            className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-colors duration-200"
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-primary-600 bg-primary-100/70 px-2 py-0.5 rounded-full">
                  {article.tags[0]}
                </span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
              </div>
              <h3 className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
                {article.title}
              </h3>
              <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-2 text-xs text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.publishDate}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function AboutSnippet() {
  return (
    <section className="bg-background-100 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              关于不良研究所
            </h2>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              不良研究所成立于2024年，是一个专注深度内容研究与创意探索的平台。我们相信，在信息爆炸的时代，真正的价值不在于"更多"，而在于"更深"。
            </p>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              我们汇聚了来自社会学、文化研究、科技评论、历史学、艺术批评等领域的独立作者和研究者，以严谨的态度和独特的视角，为读者呈现那些被主流叙事忽略的重要议题。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-1 mt-6 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              了解更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="flex-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-background-50 rounded-lg p-5 text-center">
                <p className="text-3xl font-bold text-primary-600">12+</p>
                <p className="mt-1 text-xs text-foreground-500">深度研究文章</p>
              </div>
              <div className="bg-background-50 rounded-lg p-5 text-center">
                <p className="text-3xl font-bold text-primary-600">5</p>
                <p className="mt-1 text-xs text-foreground-500">研究领域</p>
              </div>
              <div className="bg-background-50 rounded-lg p-5 text-center">
                <p className="text-3xl font-bold text-primary-600">8</p>
                <p className="mt-1 text-xs text-foreground-500">核心作者</p>
              </div>
              <div className="bg-background-50 rounded-lg p-5 text-center">
                <p className="text-3xl font-bold text-primary-600">50万+</p>
                <p className="mt-1 text-xs text-foreground-500">月均阅读量</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
      <div className="bg-background-100 rounded-lg p-8 md:p-12 text-center max-w-3xl mx-auto">
        <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600">
          <i className="ri-mail-send-line text-xl"></i>
        </div>
        <h2 className="mt-5 text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          订阅研究简报
        </h2>
        <p className="mt-3 text-sm text-foreground-600 max-w-lg mx-auto leading-relaxed">
          每周精选深度内容，第一时间送达你的邮箱。不打扰，只提供有价值的阅读。
        </p>
        <form
          data-readdy-form="true"
          action="https://readdy.ai/api/form/d9q5a7nnahb7382ov6fg"
          method="POST"
          className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        >
          <input
            type="text"
            name="company_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="form-honeypot"
          />
          <input
            type="email"
            name="email"
            placeholder="输入你的邮箱地址"
            required
            className="flex-1 px-4 py-3 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
          />
          <button
            type="submit"
            className="px-6 py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            立即订阅
          </button>
        </form>
      </div>
    </section>
  );
}